import React, { useState, useEffect } from 'react';
import { FaUpload, FaVideo, FaCheck, FaSpinner } from 'react-icons/fa';
import { useNotification } from '../context/NotificationContext';
import CardComponent from '../components/CardComponent';
import Button from '../components/Button';
import PageHeader from '../components/PageHeader';
import FileUploadField from '../components/FileUploadField';
import { uploadVideo, checkApiConfiguration } from '../api/videoUploadApi';
import { getAgenciesForSelect } from '../api/agencyApi';

interface AgencyOption {
  value: string;
  label: string;
}

const VideoUploadPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState<string>('');
  const [tenant, setTenant] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [apiConfigError, setApiConfigError] = useState<string | null>(null);
  const [agencies, setAgencies] = useState<AgencyOption[]>([]);
  const [agenciesLoading, setAgenciesLoading] = useState<boolean>(true);
  const [agenciesError, setAgenciesError] = useState<string | null>(null);
  
  const { addNotification } = useNotification();

  // Carica le agencies all'avvio
  useEffect(() => {
    const loadAgencies = async () => {
      try {
        setAgenciesLoading(true);
        setAgenciesError(null);
        
        const agencyOptions = await getAgenciesForSelect();
        setAgencies(agencyOptions);
        
        // Imposta il primo tenant come default se disponibile
        if (agencyOptions.length > 0 && !tenant) {
          setTenant(agencyOptions[0].value);
        }
        
      } catch (error) {
        console.error('Errore nel caricamento delle agencies:', error);
        setAgenciesError('Impossibile caricare la lista delle agencies');
        addNotification('error', 'Errore nel caricamento delle agencies');
      } finally {
        setAgenciesLoading(false);
      }
    };

    loadAgencies();
  }, [addNotification]);

  // Controlla la configurazione API all'avvio
  useEffect(() => {
    const { isConfigured, error } = checkApiConfiguration();
    if (!isConfigured) {
      setApiConfigError(error || 'Configurazione API non valida');
    } else {
      setApiConfigError(null);
    }
  }, []);

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setIsSuccess(false); // Reset success state when new file is selected
  };

  const handleCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCaption(e.target.value);
  };

  const handleTenantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTenant(e.target.value);
  };

  const handleUpload = async () => {
    if (!file) {
      addNotification('error', 'Seleziona un video da caricare');
      return;
    }

    if (!caption.trim()) {
      addNotification('error', 'Inserisci una caption per il video');
      return;
    }

    if (!tenant) {
      addNotification('error', 'Seleziona una agency');
      return;
    }

    // Verifica configurazione API
    const { isConfigured, error: configError } = checkApiConfiguration();
    if (!isConfigured) {
      addNotification('error', `Configurazione API non valida: ${configError}`);
      return;
    }

    setIsUploading(true);
    setIsSuccess(false);

    try {
      const result = await uploadVideo(file, caption, tenant);
      
      if (result.success) {
        setIsSuccess(true);
        addNotification('success', 'Video caricato con successo!');
        
        // Reset del form
        setFile(null);
        setCaption('');
        
        // Dopo 3 secondi, reset dello stato di successo
        setTimeout(() => {
          setIsSuccess(false);
        }, 3000);
      } else {
        throw new Error(result.error || 'Errore durante il caricamento');
      }
    } catch (error) {
      console.error('Errore nel caricamento del video:', error);
      addNotification('error', `Errore nel caricamento: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`);
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setCaption('');
    // Ripristina il primo tenant disponibile
    if (agencies.length > 0) {
      setTenant(agencies[0].value);
    }
    setIsSuccess(false);
  };

  return (
    <div>
      <PageHeader
        title="Caricamento Contenuti"
        subtitle="Carica video con caption per diversi tenant"
        icon={<FaVideo />}
      />

      <div className="max-w-4xl mx-auto">
        {/* Avviso configurazione API */}
        {apiConfigError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-red-700">
              <span className="text-red-600 font-bold">⚠️</span>
              <span className="font-medium">Configurazione API richiesta</span>
            </div>
            <div className="mt-2 text-sm text-red-600">
              <p><strong>Errore:</strong> {apiConfigError}</p>
              <p className="mt-1">
                <strong>Soluzione:</strong> Configura la variabile <code className="bg-red-100 px-1 rounded">REACT_APP_API_KEY</code> nel file <code className="bg-red-100 px-1 rounded">.env</code> del frontend con una chiave API valida.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form di Upload */}
          <div className="lg:col-span-2">
            <CardComponent title="📁 Carica Nuovo Video">
              <div className="space-y-4">
                {/* File Upload */}
                <FileUploadField
                  id="video-upload"
                  accept="video/mp4,video/mov"
                  onChange={handleFileChange}
                  label="Seleziona Video"
                />

                {/* Caption Input */}
                <div>
                  <label htmlFor="caption" className="block text-sm font-medium text-gray-700 mb-2">
                    Caption *
                  </label>
                  <textarea
                    id="caption"
                    value={caption}
                    onChange={handleCaptionChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Inserisci la descrizione del video..."
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-sm text-gray-500">
                      Descrizione che accompagnerà il video
                    </p>
                    <p className="text-sm text-gray-400">
                      {caption.length}/500
                    </p>
                  </div>
                </div>

                {/* Tenant Selection */}
                <div>
                  <label htmlFor="tenant" className="block text-sm font-medium text-gray-700 mb-2">
                    Tenant *
                  </label>
                  <select
                    id="tenant"
                    value={tenant}
                    onChange={handleTenantChange}
                    disabled={agenciesLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {agenciesLoading && (
                      <option value="">Caricamento agencies...</option>
                    )}
                    {!agenciesLoading && agencies.length === 0 && (
                      <option value="">Nessuna agency disponibile</option>
                    )}
                    {!agenciesLoading && agencies.length > 0 && !tenant && (
                      <option value="">Seleziona una agency</option>
                    )}
                    {agencies.map((agency) => (
                      <option key={agency.value} value={agency.value}>
                        {agency.label}
                      </option>
                    ))}
                  </select>
                  {agenciesError && (
                    <p className="text-sm text-red-500 mt-1">
                      {agenciesError}
                    </p>
                  )}
                  {!agenciesError && (
                    <p className="text-sm text-gray-500 mt-1">
                      Seleziona l'agency di destinazione per il video
                    </p>
                  )}
                </div>

                {/* Status e Informazioni File */}
                {file && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-blue-700">
                      <FaVideo className="text-blue-600" />
                      <span className="font-medium">File selezionato:</span>
                    </div>
                    <div className="mt-2 text-sm text-blue-600">
                      <p><strong>Nome:</strong> {file.name}</p>
                      <p><strong>Dimensione:</strong> {(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                      <p><strong>Tipo:</strong> {file.type}</p>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {isSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-green-700">
                      <FaCheck className="text-green-600" />
                      <span className="font-medium">Video caricato con successo!</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Il video è stato salvato per il tenant <strong>{tenant}</strong>
                    </p>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    onClick={handleUpload}
                    isLoading={isUploading}
                    disabled={isUploading || !file || !caption.trim() || !tenant || isSuccess || !!apiConfigError || agenciesLoading}
                    className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 flex-1 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isUploading ? (
                      <>
                        <FaSpinner className="mr-2 animate-spin" /> Caricamento...
                      </>
                    ) : isSuccess ? (
                      <>
                        <FaCheck className="mr-2" /> Caricato
                      </>
                    ) : (
                      <>
                        <FaUpload className="mr-2" /> Carica Video
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={resetForm}
                    disabled={isUploading}
                    variant="secondary"
                    className="px-6 py-3"
                  >
                    Reset Form
                  </Button>
                </div>
              </div>
            </CardComponent>
          </div>

          {/* Sidebar con informazioni */}
          <div className="lg:col-span-1">
            <CardComponent title="ℹ️ Informazioni Upload">
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Formati Supportati</h4>
                  <div className="text-gray-600 space-y-1">
                    <p>• Video MP4</p>
                    <p>• Video MOV</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Specifiche Tecniche</h4>
                  <div className="text-gray-600 space-y-1">
                    <p>• Dimensione: Illimitata</p>
                    <p>• Durata: Nessun limite</p>
                    <p>• Risoluzione: Qualsiasi</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Agencies Disponibili</h4>
                  <div className="text-gray-600 space-y-1">
                    {agenciesLoading ? (
                      <p className="text-gray-400 italic">Caricamento...</p>
                    ) : agencies.length > 0 ? (
                      agencies.map((agency) => (
                        <p key={agency.value}>
                          <strong>{agency.label}</strong>
                        </p>
                      ))
                    ) : (
                      <p className="text-red-500 italic">Nessuna agency disponibile</p>
                    )}
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <h4 className="font-medium text-yellow-800 mb-1">⚠️ Nota</h4>
                  <p className="text-yellow-700 text-xs">
                    Assicurati di aver configurato la tua API-KEY nelle variabili d'ambiente per utilizzare questa funzionalità.
                  </p>
                </div>
              </div>
            </CardComponent>

            <CardComponent title="📊 Status Sistema" className="mt-4">
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">API Status:</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-600 font-medium">Online</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Upload Service:</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-600 font-medium">Attivo</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Storage:</span>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-600 font-medium">Disponibile</span>
                  </div>
                </div>
              </div>
            </CardComponent>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUploadPage;